import React, { useEffect, useState, useCallback, forwardRef } from "react";
import { Row, Col, Dropdown } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate, useLocation } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";
import SearchWithAPI from "../common/search-box-new";

// Shared font/color utility classes for the table (fs-*, fw-*, text-dkblue, text-ltgrey)
import "../common/Util.css";
import "./order.css";

// API Imports
// import { getOrderListApi, bulkDeleteOrderApi } from "./order-api";
import * as orderApi from "./order-api";
import { sanitizeInput } from "../common/security-utils";

// SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import CalendarIcon from "../../assets/svg/calendar.svg";
import Plus from "../../assets/svg/add.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
import DeleteModal from "../common/delete-modal.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";
import AiChat from "../common/ai-chat.js";
import { askOrderChatApi, getOrderAiProvidersApi } from "./order-api";

// Persist list UI state (search, pagination, date range, filters) across
// navigation to a view page and back. Uses sessionStorage so it survives
// refresh but clears when the browser tab is closed.
const ORDER_LIST_STATE_KEY = "orderListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(ORDER_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const OrderList = () => {
  const options = ["Order No.","Title", "Customer", "Type", "Amount", "Status", "Date"];
  const persisted = loadPersistedState();

  // Inputs
  // ... (add filter states if needed)

  // Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Navigate
  const navigate = useNavigate();
  const location = useLocation();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Order");
  const hasEditPermission = canEdit(rolePermissionList, "Order");
  const hasDeletePermission = canDelete(rolePermissionList, "Order");
  const hasViewPermission = canView(rolePermissionList, "Order");

  // Search
  const [search, setSearch] = useState(persisted.search ?? "");

  // Filters: Status (dropdown), Type + Customer (searchable API dropdowns)
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedStatus, setSelectedStatus] = useState(
    persisted.selectedStatus ?? { value: "", label: "All" },
  );
  const [selectedType, setSelectedType] = useState(persisted.selectedType ?? null);
  const [customerFilter, setCustomerFilter] = useState(persisted.customerFilter ?? null);

  // Date range filter (From date - To date), shown as a two-month calendar
  const [startDate, setStartDate] = useState(
    persisted.startDate ? new Date(persisted.startDate) : null,
  );
  const [endDate, setEndDate] = useState(
    persisted.endDate ? new Date(persisted.endDate) : null,
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const formatYearMonthDay = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setItemOffset(0);
    if (start && end) {
      setIsDatePickerOpen(false);
    }
  };

  const CalendarCustomInput = forwardRef(({ onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDatePickerOpen((prev) => !prev);
      }}
      title="Filter by Date"
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border: "1px solid #e5e5e5",
        background: startDate ? "#3D4AF5" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
        outline: "none",
      }}
    >
      <img
        src={CalendarIcon}
        alt="Calendar"
        style={{
          width: "18px",
          height: "18px",
          display: "block",
          margin: "auto",
          filter: startDate ? "brightness(0) invert(1)" : "none",
          flexShrink: 0,
        }}
      />
    </button>
  ));

  const statusOption = [
    { value: "", label: "All" },
    { value: "Created", label: "Created" },
    { value: "Confirmed", label: "Confirmed" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Delivered", label: "Delivered" },
    { value: "Invoiced", label: "Invoiced" },
    
  ];
  // Clear (✕) icon shared by the Type / Customer searchable filters.
  const clearIconStyle = {
    position: "absolute",
    right: "34px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#e5e5e5",
    color: "#555",
    fontSize: "12px",
    lineHeight: "18px",
    textAlign: "center",
    cursor: "pointer",
    zIndex: 5,
    userSelect: "none",
  };

  // Create Order Dropdown
  const [open, setOpen] = useState(false);

  // Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  const columnsGridTemplate = visibleOptions.map(() => "1fr").join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  // Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setIsBulkDelete(false);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsBulkDelete(false);
    setSnackbarMessage(sanitizeInput(response.message ?? ""));
    setSnackbarStatus(Boolean(response.success));
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      setSelectedRows([]);
      setAllSelected(false);
      getOrderList();
    }
  };

  const handleBulkDelete = () => {
    // 🔒 Permission check for delete
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete orders");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setIsOpenDelete(true);
  };

  // Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // Get Order List
  const [orderList, setOrderList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const getOrderList = async (filters = null) => {
    setListLoading(true);
    try {
      const safeSearch = encodeURIComponent(sanitizeInput(search));
      const safeFilters =
        typeof filters === "string" ? filters.replace(/[<>"']/g, "") : "";

      // Status / Type / Customer / Date filter params
      const filterParams = [
        selectedStatus?.value
          ? `&status=${encodeURIComponent(selectedStatus.value)}`
          : "",
        selectedType?.value
          ? `&order_type=${encodeURIComponent(selectedType.value)}`
          : "",
        customerFilter?.value
          ? `&customer_id=${encodeURIComponent(customerFilter.value)}`
          : "",
        startDate && endDate
          ? `&from_date=${formatYearMonthDay(startDate)}&to_date=${formatYearMonthDay(endDate)}`
          : "",
      ].join("");

      const response = await orderApi.getOrdersListApi(
        `?search=${safeSearch}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}${filterParams}${safeFilters}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        const formatted = formatOrderData(response.data.orders);
        setOrderList(formatted);
      }
      return response.data;
    } catch (err) {
      setSnackbarMessage("Failed to fetch orders. Please try again.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    setSelectedRows([]);
    setAllSelected(false);
    getOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, itemsPerPage, itemOffset, selectedStatus, selectedType, customerFilter, startDate, endDate]);

  // Amount is stored pre-formatted (e.g. "₹ 1,00,000.00"), so a plain string
  // compare sorts "10,000" before "1,062" (lexical, not numeric). Strip
  // everything but digits/decimal point and compare as numbers instead.
  const parseAmount = (value) => Number.parseFloat(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0;

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedOrderList = React.useMemo(() => {
    if (!sortColumn) return orderList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...orderList].sort((a, b) => {
      if (sortColumn === "Amount") {
        return (parseAmount(a[sortColumn]) - parseAmount(b[sortColumn])) * dir;
      }
      const aStr = (a[sortColumn] ?? "").toString().toLowerCase();
      const bStr = (b[sortColumn] ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [orderList, sortColumn, sortDirection]);

  // When the Order sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setItemOffset(0);
      setItemsPerPage(10);
      setStartDate(null);
      setEndDate(null);
      setSelectedStatus({ value: "", label: "All" });
      setSelectedType(null);
      setCustomerFilter(null);
      sessionStorage.removeItem(ORDER_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        ORDER_LIST_STATE_KEY,
        JSON.stringify({
          search,
          itemOffset,
          itemsPerPage,
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null,
          selectedStatus,
          selectedType,
          customerFilter,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, itemOffset, itemsPerPage, startDate, endDate, selectedStatus, selectedType, customerFilter]);

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Format an amount with the row's currency symbol (falls back to ₹).
  const formatAmount = (value, symbol) => {
    const num = Number.parseFloat(value);
    const display = Number.isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    return `${decodeHtmlEntity(symbol) || "₹"} ${display}`;
  };

  const formatOrderData = (rawOrders) => {
    return rawOrders.map((order) => ({
      id: order.id,
      "Order No.": sanitizeInput(order.order_no ?? ""),
      Title: sanitizeInput(order.title ?? ""),
      Customer: sanitizeInput(order.customer_name ?? order.customer ?? ""),
      Type: sanitizeInput(order.type ?? order.order_type ?? ""),
      Amount: formatAmount(
        order.amount ?? order.total_amount ?? "",
        order.currency_symbol,
      ),
      Status: sanitizeInput(order.status ?? ""),
      Date: sanitizeInput(order.date ?? ""),
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    const selectable = orderList.filter((order) => order.Status === "Created");
    if (selectable.length > 0) {
      setAllSelected(selectable.every((item) => selectedRows.includes(item.id)));
    } else {
      setAllSelected(false);
    }
  }, [selectedRows, orderList]);

  // handle checkbox logic
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      const selectableIds = orderList
        .filter((order) => order.Status === "Created")
        .map((order) => order.id);
      setSelectedRows(selectableIds);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Copy to clipboard function
  const handleCopyOrderNo = (orderNo) => {
    navigator.clipboard.writeText(orderNo).then(() => {
      setSnackbarMessage(`✓ Copied: ${orderNo}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isOpenDelete && (
          <DeleteModal
            deleteUrl={isBulkDelete ? null : `order/delete/${deleteId}`}
            bulkDeleteApi={isBulkDelete ? orderApi.bulkDeleteOrdersApi : null}
            bulkDeleteIds={isBulkDelete ? selectedRows : null}
            count={isBulkDelete ? selectedRows.length : null}
            onSuccess={onSuccessDelete}
            onCancel={cancelDelete}
          />
        )}
        <div style={{ marginBottom: "16px" }}>
          <div className="d-flex justify-content-between g-3">
            <div>
              {/* Leads count */}

              <div
                style={{
                  fontWeight: "500",
                  color: "#172b4d",
                  fontSize: "20px",
                }}
              >
                Order - {records}
              </div>
            </div>
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ flexWrap: "wrap", gap: "12px" }}
            >
              {/* Search box */}

              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "100px",
                    border: "0.8px solid #e5e5e5",
                    padding: "9px 16px",
                    backgroundColor: "#ffffff",
                    height: "36px",
                  }}
                >
                  <img src={Search} alt="search" style={{ width: "16px" }} />
                  <input
                    type="search"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                    style={{
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      flex: 1,
                      backgroundColor: "transparent",
                    }}
                    className="no-focus-border"
                  />
                </div>
                {/* <img
                  src={Filter}
                  style={{ height: "36px", width: "36px", cursor: "pointer" }}
                  onClick={() => setShowFilter(true)}
                /> */}
              </div>

              {/* Status filter */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="d-flex align-items-center justify-content-between"
                  style={{ borderRadius: "12px", padding: "6px 12px", backgroundColor: "#F6F6F6" }}
                >
                  <span className="fs-12 fw-400 text-dkblue">Status :</span>
                  <span className="fs-13 fw-500 textblue px-1">
                    {selectedStatus?.label || "All"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    border: "1px solid #EBEBEB",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusOption.map((option) => (
                    <Dropdown.Item
                      className="fs-14 fw-500"
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setItemOffset(0);
                      }}
                      active={selectedStatus?.value === option.value}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* Type filter */}
              <div style={{ width: "200px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  onDropdownChange={(option) => {
                    setSelectedType(option);
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_order_type"}
                  placeholder={"Filter by type"}
                  selectedOption={selectedType}
                />
                {selectedType?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedType(null);
                      setItemOffset(0);
                    }}
                    title="Clear type filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Customer filter */}
              <div style={{ width: "240px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  onDropdownChange={(option) => {
                    setCustomerFilter(option);
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_customers"}
                  placeholder={"Filter by customer"}
                  selectedOption={customerFilter}
                />
                {customerFilter?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomerFilter(null);
                      setItemOffset(0);
                    }}
                    title="Clear customer filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* Date range filter — two-month calendar */}
              <div className="order-date-filter" style={{ width: "40px", height: "40px", flexShrink: 0, position: "relative" }}>
                <DatePicker
                  selectsRange
                  monthsShown={2}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  isClearable
                  customInput={<CalendarCustomInput />}
                  calendarClassName="order-date-range-picker"
                  popperPlacement="bottom-end"
                  open={isDatePickerOpen}
                  onClickOutside={() => setIsDatePickerOpen(false)}
                />
              </div>

              {/* ✅ COMMON FILTER */}
              {/*  <CommonFilter
                     type="leads"
                     open={showFilter}
                     onClose={() => setShowFilter(false)}
                     onSubmit={handleFilter}
                   /> */}

              {/* <Col xs={2} md={1} className="d-flex justify-content-center"> */}
              {/* ✅ COMMON FILTER */}
              {/*  <ImportFileModal
                     type="leads"
                     open={showImport}
                     onClose={() => setShowImport(false)}
                     onExport={downloadTemplate}
                     onImport={(base64) => uploadTicket(base64)}
                   /> */}
              {/* </Col> */}

              {/* Create button with dropdown */}

              {
                <div className="d-flex justify-content-end align-items-center">
                  {selectedRows.length === 0 && hasCreatePermission && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 16px",
                          borderRadius: "100px",
                          borderBottom: "3px solid #1723C2",
                          background:
                            "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                          height: "40px",
                          width: "fit-content",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onClick={() => navigate("/order/create")}
                        >
                          <img
                            src={Plus}
                            style={{ width: "16px" }}
                            alt="Plus"
                          />
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "16px",
                              color: "#ffffff",
                            }}
                          >
                            Create
                          </div>
                        </div>
                        {/* <div
                             onClick={() => setOpen(!open)}
                             style={{ display: "flex", alignItems: "center" }}
                           >
                             <div
                               style={{
                                 width: "1px",
                                 height: "40px",
                                 backgroundColor: "#ffffff",
                               }}
                             ></div>
                             <div style={{ paddingLeft: "16px" }}>
                               <img
                                 src={Dropdown}
                                 style={{ width: "10px" }}
                                 alt="Dropdown"
                               />
                             </div>
                           </div> */}
                      </div>

                      {/* {open && (
                           <div
                             style={{
                               position: "absolute",
                               top: "50px",
                               backgroundColor: "#ffffff",
                               padding: "16px",
                               borderRadius: "16px",
                               boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                               zIndex: 1000,
                               width: "100%",
                             }}
                           >
                             <div style={{ padding: "8px 0" }}>Lead</div>
                             <div style={{ padding: "8px 0" }}>Contact</div>
                             <div style={{ padding: "8px 0" }}>Account</div>
                             <div style={{ padding: "8px 0" }}>Deal</div>
                           </div>
                         )} */}
                    </div>
                  )}
                  {selectedRows.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        padding: "0 20px",
                        borderRadius: "50px",
                        borderBottom: "3px solid #B31717",
                        background:
                          "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                        height: "40px",
                        width: "auto",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setIsBulkDelete(true);
                        setIsOpenDelete(true);
                      }}
                    >
                      <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
                    </div>
                  )}
                  {/*  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                      marginLeft: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 16px",
                        borderRadius: "100px",
                        border: "1px solid #3D4AF5",
                        height: "36px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                    >
                       <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onClick={() => setShowImport(true)}
                      >
                        <img
                          src={ExcelImportIC}
                          style={{ width: "16px" }}
                          alt="Import"
                        />
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            color: "#3D4AF5",
                          }}
                        >
                          Import
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>
              }
            </div>
          </div>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      padding: "10px 24px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={orderList.filter((order) => order.Status === "Created").length === 0}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                    {visibleOptions.map((label) => (
                      <div
                        key={label}
                        onClick={() => handleSortClick(label)}
                        title={`Sort by ${label}`}
                        style={{
                          minWidth: 0,
                          padding: "10px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderRight: "1px solid #ebebeb",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                          <div className="fs-12 fw-600 text-ltgrey" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {label.toUpperCase()}
                          </div>
                          <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                            {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                          </span>
                        </div>
                        <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                      </div>
                    ))}
                  </div>
                  <div
                    onClick={() => setShowModal(true)}
                    style={{
                      width: "72px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img src={Customize} alt="customize" />
                  </div>
                </div>
                {/* Data Rows */}
                {listLoading ? (
                  <div style={{ width: "100%", padding: "40px 0" }}>
                    <Loader height="120px" />
                  </div>
                ) : sortedOrderList.length === 0 ? (
                  <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                    No orders found
                  </div>
                ) : (
                  sortedOrderList.map((order, rowIndex) => (
                  <div
                    key={order.id}
                    onMouseEnter={() => setHoveredIndex(rowIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                      backgroundColor: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: "72px",
                        padding: "10px 24px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(order.id)}
                        onChange={() => toggleRow(order.id)}
                        disabled={!anyColumnSelected || order.Status !== "Created"}
                      />
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                      onClick={() => {
                        if (hasViewPermission) {
                          navigate("/order/view", {
                            state: { id: order.id },
                          });
                        }
                      }}
                    >
                      {visibleOptions.map((label) => (
                        <div
                          key={label}
                          title={order[label]}
                          className="text-dkblue fs-14 fw-400"
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ebebeb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {order[label] ?? "-"}
                          </div>
                          {label === "Order No." && order[label] && (
                            <img
                              src={Copy}
                              alt="Copy"
                              style={{
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                                flexShrink: 0,
                                opacity: 0.6,
                                transition: "opacity 0.2s",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyOrderNo(order[label]);
                              }}
                              onMouseEnter={(e) => (e.target.style.opacity = "1")}
                              onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
                              title="Copy order number"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ width: "72px", flexShrink: 0 }} />
                    {hoveredIndex === rowIndex && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {order.Status === "Created" && hasEditPermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/order/create", {
                                  state: { id: order.id },
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Edit Order"
                            >
                              <EditRow />
                            </div>
                          )}
                          <span style={{ marginLeft: "16px" }}></span>
                          {order.Status === "Created" && hasDeletePermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(order.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Delete Order"
                            >
                              <DeleteRow />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}
                {showModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                        width: "300px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        Select Columns
                      </div>
                      {options.map((label, index) => (
                        <label
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedColumns[label]}
                            onChange={() => toggleColumn(label)}
                            style={{ marginRight: "8px" }}
                          />
                          {label}
                        </label>
                      ))}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          marginTop: "16px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#3D4AF5",
                          color: "#fff",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(Number(event.target.value));
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  // Keyed on itemOffset so the (class-based) component fully
                  // remounts on every page change — its internal "selected"
                  // state is only initialized from forcePage in the
                  // constructor, and componentDidUpdate's forcePage diff
                  // check doesn't reliably catch a value that was already
                  // correct on the very first render (e.g. itemOffset
                  // restored from sessionStorage on mount), which otherwise
                  // leaves the active page highlight stuck on page 1.
                  key={itemOffset}
                  previousLabel={
                    <img src={lar} className="ar3" alt="previous" />
                  }
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  forcePage={itemOffset}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>
          <ToastContainer position="top-end">
            <Toast
              className="d-inline-block m-1"
              bg={snackbarStatus === true ? "success" : "danger"}
              show={showSnackbar}
              onClose={handleSnackbarClose}
              delay={3000}
              autohide
            >
              <Toast.Body style={{ color: "#ffffff" }}>
                {snackbarMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      <AiChat
        askApi={askOrderChatApi}
        providersApi={getOrderAiProvidersApi}
        title="Ask about orders"
        hint="Try: “what is the latest order?” or “break down orders by status”"
      />
      </div>
    </div>
  );
};

export default OrderList;
